/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

export interface QHistoryEntry
{
   iconName: string;
   label: string;
   path: string;
   date?: Date;
}

export interface QHistory
{
   entries: QHistoryEntry[];
}


export default class HistoryUtils
{
   private static LS_KEY = "qqq.history";


   /*******************************************************************************
    ** Strip a URL hash from a path - we never want the recently-viewed list to
    ** treat /order/5 and /order/5#audit as distinct entries.  Normalizing here
    ** keeps the invariant local to this util so callers can't accidentally
    ** introduce hash-suffixed paths.
    *******************************************************************************/
   private static stripHash = (path: string): string =>
   {
      return (path ?? "").replace(/#.*$/, "");
   };


   /*******************************************************************************
    ** Push an entry into the history
    *******************************************************************************/
   public static push = (entry: QHistoryEntry) =>
   {
      const history = HistoryUtils.get();

      if(!entry.date)
      {
         entry.date = new Date()
      }

      entry.path = HistoryUtils.stripHash(entry.path);

      ////////////////////////////////////////////////////////////////////////////////
      // also normalize any already-stored paths so a previously-saved hash entry   //
      // (e.g., /order/5#audit) gets folded into the new /order/5 entry rather than //
      // sticking around as a phantom duplicate.  iterate backwards because we may  //
      // now have multiple matches (the prior /order/5 plus /order/5#audit).        //
      ////////////////////////////////////////////////////////////////////////////////
      for (let i = history.entries.length - 1; i >= 0; i--)
      {
         history.entries[i].path = HistoryUtils.stripHash(history.entries[i].path);
         if(history.entries[i].path == entry.path)
         {
            history.entries.splice(i, 1);
         }
      }

      history.entries.push(entry);

      if(history.entries.length > 20)
      {
         history.entries.splice(0, history.entries.length - 3);
      }

      localStorage.setItem(HistoryUtils.LS_KEY, JSON.stringify(history));
   };



   /*******************************************************************************
    ** Get the history
    *******************************************************************************/
   public static get = (): QHistory =>
   {
      const existingJSON = localStorage.getItem(HistoryUtils.LS_KEY);
      const history: QHistory = existingJSON ? JSON.parse(existingJSON) : {}
      if(!history.entries)
      {
         history.entries = [];
      }

      return (history);
   };


   /*******************************************************************************
    ** make sure a specific path isn't in the history (e.g., after a 404)
    *******************************************************************************/
   public static ensurePathNotInHistory(path: string)
   {
      const history = HistoryUtils.get();
      path = HistoryUtils.stripHash(path);

      for (let i = history.entries.length - 1; i >= 0; i--)
      {
         if(HistoryUtils.stripHash(history.entries[i].path) == path)
         {
            history.entries.splice(i, 1);
         }
      }

      localStorage.setItem(HistoryUtils.LS_KEY, JSON.stringify(history));
   }
}

